import { BusinessSchema } from '@40seas/entities/businesses';
import { Buyer } from '@40seas/entities/buyers';
import {
    EntityReferenceWithAmount,
    EntityReferenceWithAmountSchema,
    EnumSchema,
    Executor,
    FromSchema,
    ZodDate,
} from '@40seas/entities/common';
import { Seller } from '@40seas/entities/sellers';
import { UserRole } from '@40seas/entities/users';
import {
    Address,
    AddressSchema,
    BaseEntity,
    Currency,
    Discount,
    DiscountSchema,
    Fee,
    FeeSchema,
    LineItem,
    LineItemSchema,
    PaymentOptionWithFee,
    PaymentOptionWithFeeSchema,
    PaymentTermsOptions,
    PaymentTermType,
    PercentageValue,
    PercentageValueSchema,
    WithBaseEntitySchema,
} from '@40seas/models';
import { z } from 'zod';
import { CheckoutMode } from './checkout.mode.enum';
import { CheckoutResult, CheckoutResultSchema } from './checkout.result';
import { CheckoutStatus } from './checkout.status.enum';
import { ConfirmationMode } from './confirmation-mode.enum';
import { LinkedCheckoutReason } from './linked-checkout-reason.enum';

export const LinkedCheckoutSchema = z
    .object({
        checkout: z
            .string()
            .or(z.lazy(() => CheckoutSchema))
            .optional(),
        reason: EnumSchema(LinkedCheckoutReason).optional(),
    })
    .openapi({ ref: 'LinkedCheckout' })
    .strict();

export const LinkedCheckoutRefSchema = z
    .object({
        checkout: z.string(), // Reference to another checkout by ID
        reason: EnumSchema(LinkedCheckoutReason).optional(),
    })
    .openapi({ ref: 'LinkedCheckoutRef' })
    .strict();

export class LinkedCheckout extends FromSchema(LinkedCheckoutSchema) {
    checkout?: string | Checkout;
    reason?: LinkedCheckoutReason;
}

export const CheckoutSchema = WithBaseEntitySchema(
    z.object({
        externalId: z.string().optional(),
        buyer: z
            .string()
            .regex(/^[0-9a-f]{24}$/i)
            .or(BusinessSchema),
        amount: z
            .number()
            .min(0.01, { message: 'Amount must be at least 0.01' })
            .max(10000000)
            .optional()
            .refine(
                (value) => {
                    if (value === undefined) return true; // Skip validation for undefined
                    // Check if the number has no more than 2 decimal places
                    const parts = value.toString().split('.');
                    return parts.length <= 1 || parts[1].length <= 2;
                },
                {
                    message: 'Amount must have no more than 2 decimal places',
                },
            ),
        currency: EnumSchema(Currency)
            .transform((currency) => (currency === Currency.CNH ? Currency.CNY : currency))
            .openapi({
                effectType: 'input',
                description: 'Note: CNH input will be transformed to CNY as we receive in request CNH but support CNY',
            }),
        amountPaid: z.number().optional(),
        shippingFee: z.number().optional().or(z.null()),
        paymentFees: z.array(FeeSchema).optional(),
        feesBalance: z.number().nullish(),
        linkedCheckouts: z
            .array(LinkedCheckoutSchema)
            .optional()
            .openapi({
                description: 'A list of linked checkouts represented as references in OpenAPI to avoid recursion.',
                items: { $ref: '#/components/schemas/LinkedCheckoutRef' }, // Override $ref in OpenAPI
                // Override $ref in OpenAPI
            }),
        tax: PercentageValueSchema.optional().or(z.null()),
        paymentTerms: z.array(EnumSchema(PaymentTermType)).optional(),
        paymentTermsOptions: z.any().optional(),
        paymentOptions: z.array(PaymentOptionWithFeeSchema).optional(),
        lineItems: z.array(LineItemSchema).optional(),
        shippingAddress: AddressSchema.optional(),
        billingAddress: AddressSchema.optional(),
        reference: z.string().optional(),
        seller: z
            .string()
            .regex(/^[0-9a-f]{24}$/i)
            .or(BusinessSchema),
        result: CheckoutResultSchema.nullish(),
        status: EnumSchema(CheckoutStatus),
        invoice: z.string().optional(),
        invoices: z.array(EntityReferenceWithAmountSchema).optional(),
        dueDate: ZodDate.optional(),
        discounts: z.array(DiscountSchema).optional(),
        balance: z.number().optional(),
        confirmationMode: EnumSchema(ConfirmationMode).optional(),
        mode: EnumSchema(CheckoutMode).optional(),
        confirmedBy: EnumSchema(UserRole).optional(),
    }),
)
    .openapi({ ref: 'Checkout' })
    .strict();

export class Checkout extends FromSchema(CheckoutSchema) implements BaseEntity {
    id: string;
    externalId?: string;
    buyer: string | Buyer;
    status: CheckoutStatus;
    amount?: number;
    currency: Currency;
    amountPaid?: number;
    shippingFee?: number;
    paymentFees?: Fee[];
    feesBalance?: number;
    linkedCheckouts?: LinkedCheckout[];
    tax?: PercentageValue;
    paymentTerms?: PaymentTermType[];
    paymentTermsOptions?: PaymentTermsOptions;
    paymentOptions?: PaymentOptionWithFee[];
    lineItems?: LineItem[];
    shippingAddress?: Address;
    billingAddress?: Address;
    seller: string | Seller;
    reference?: string;
    result?: CheckoutResult;
    invoice?: string;
    invoices?: EntityReferenceWithAmount[];
    dueDate?: Date;
    discounts?: Discount[];
    balance?: number;
    confirmationMode?: ConfirmationMode;
    confirmedBy?: UserRole;
    mode?: CheckoutMode;
    createdAt: Date;
    updatedAt: Date;
    tenantId: string;
    createdBy: Executor;
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
}

export class MultiTenantCheckout extends Checkout {}
